/**
 * ZIP ↔ city/state check through the Smarty US ZIP Code API, one lookup per
 * address block when a form submits.
 *
 *  - A frontend data-quality gate only: the server stays authoritative on the
 *    submitted payload and never sees the result.
 *  - It fails open. An empty embedded key, a blank city, state or ZIP, a
 *    transport error, a non-2xx, a timeout or a missing result all return
 *    `"ok"`, so a Smarty outage can never block a submit; the form's own
 *    required and format rules own blanks. Only a result carrying a Smarty
 *    `status` (`invalid_city`, `invalid_zipcode`, `conflict`, ...) is a
 *    `"mismatch"`. Lookup errors are swallowed quietly (`console.debug` at most).
 *  - Lookups go from the browser straight to Smarty with the embedded key,
 *    which allows GET only; each call sends a single lookup, never a batch.
 *  - With `useAddressAutocomplete` this is the only Smarty touchpoint.
 */

import { ClientBuilder, SharedCredentials, usZipcode } from "smartystreets-javascript-sdk";

type UsZipcodeClient = ReturnType<ClientBuilder["buildUsZipcodeClient"]>;

/** One attempt, bounded: a retrying lookup would stall the submit it gates. */
const TIMEOUT_MS = 5000;

/** One SDK client per embedded key for the lifetime of the page. */
const clientCache = new Map<string, UsZipcodeClient>();

function clientFor(embeddedKey: string): UsZipcodeClient {
  let client = clientCache.get(embeddedKey);
  if (!client) {
    client = new ClientBuilder(new SharedCredentials(embeddedKey))
      .withMaxRetries(0)
      .withMaxTimeout(TIMEOUT_MS)
      .buildUsZipcodeClient();
    clientCache.set(embeddedKey, client);
  }
  return client;
}

export async function checkZipCityState(
  embeddedKey: string,
  address: { city: string; state: string; zip: string },
): Promise<"ok" | "mismatch"> {
  const city = address.city.trim();
  const state = address.state.trim();
  // Only the 5-digit prefix of a ZIP+4 is looked up.
  const zip = address.zip.trim().slice(0, 5);
  if (embeddedKey === "" || city === "" || state === "" || zip === "") return "ok";
  // The SDK pushes each result onto the lookup itself, not the returned batch.
  const lookup = new usZipcode.Lookup(city, state, zip);
  try {
    await clientFor(embeddedKey).send(lookup);
  } catch (err) {
    console.debug("zip city/state lookup failed", err);
    return "ok";
  }
  const result = lookup.result[0];
  return result !== undefined && !result.valid ? "mismatch" : "ok";
}

/**
 * Fills `{city}` and `{state}` in the `err.zipMismatch` catalog string; the
 * `t()` layer has no interpolation.
 */
export function zipMismatchMessage(template: string, city: string, state: string): string {
  return template.replace("{city}", city).replace("{state}", state);
}
